/**
 * Helpers for the branch-scoped customer-fees API (tecfit-backend).
 * Backend route: POST /api/v1/branches/:branchId/customer-fees/actions/pay-request-fund (AddReqFeeDto).
 *
 * The backend ValidationPipe runs with `forbidNonWhitelisted`, so any extra
 * fields the UI fund object carries (isDelete, isSynced, currentUserData, etc.)
 * must be stripped. Several DTO fields are also typed as strings, so numeric /
 * Date values are coerced before sending.
 */

import { getSplitGroupFees } from './split-payment.util';

function toStr(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

/** Body for POST .../customer-fees/actions/pay-request-fund (AddReqFeeDto). */
export function buildPayReqFundPayload(fund: Record<string, unknown>): Record<string, unknown> {
  const f = (fund ?? {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {};

  const setStr = (key: string, value: unknown) => {
    const s = toStr(value);
    if (s !== undefined) payload[key] = s;
  };

  setStr('memberId', f.memberId);
  setStr('scope', f.scope);
  setStr('type', f.type);
  setStr('initiatedFrom', f.initiatedFrom);
  setStr('createdBy', f.createdBy);
  setStr('oldDate', f.oldDate);
  setStr('dueDate', f.dueDate);
  setStr('invoice', f.invoice);
  setStr('tax', f.tax);
  setStr('discount', f.discount);
  setStr('status', f.status);
  setStr('month', f.month);
  setStr('year', f.year);
  setStr('day', f.day);
  setStr('description', f.description);
  setStr('feeSubmissionReceiptUrl', f.feeSubmissionReceiptUrl);
  setStr('feeReceiptImageUrl', f.feeReceiptImageUrl);
  setStr('transactionNumber', f.transactionNumber);
  setStr('paymentMode', f.paymentMode);
  setStr('collectedFromRequestManagement', f.collectedFromRequestManagement);
  setStr('billingPeriodStart', f.billingPeriodStart);
  setStr('billingPeriodEnd', f.billingPeriodEnd);

  if (f.date !== undefined && f.date !== null) payload.date = f.date;
  if (f.pay !== undefined && f.pay !== null) payload.pay = f.pay;
  if (f.noOfMonths !== undefined && f.noOfMonths !== null) payload.noOfMonths = f.noOfMonths;
  if (f.isApproved !== undefined && f.isApproved !== null) payload.isApproved = f.isApproved;
  if (f.payAdvance === true || f.payAdvance === 'true') payload.payAdvance = true;
  if (f.payNewPeriod === true || f.payNewPeriod === 'true') payload.payNewPeriod = true;
  if (f.sameHistoryGroupPayment === true || f.sameHistoryGroupPayment === 'true') {
    payload.sameHistoryGroupPayment = true;
  }

  return payload;
}

/** Body for POST .../customer-fees/actions/delete-fee (DeleteFeeDto). */
export function buildDeleteFeePayload(fee: Record<string, unknown>): Record<string, unknown> {
  const f = (fee ?? {}) as Record<string, unknown>;
  const feeId = f._id ?? f.id;
  const payload: Record<string, unknown> = {
    _id: String(feeId),
  };

  const setStr = (key: string, value: unknown) => {
    const s = toStr(value);
    if (s !== undefined) payload[key] = s;
  };

  setStr('customerID', f.customerID);
  setStr('status', f.status);
  setStr('previousDueDate', f.previousDueDate);
  setStr('branch', f.branch);
  setStr('type', f.type);
  setStr('scope', f.scope);
  setStr('initiatedFrom', f.initiatedFrom);
  setStr('name', f.name);
  setStr('tagNumber', f.tagNumber);

  if (f.noOfMonths !== undefined && f.noOfMonths !== null && f.noOfMonths !== '') {
    payload.noOfMonths = Number(f.noOfMonths);
  }
  if (f.createdOn !== undefined && f.createdOn !== null && f.createdOn !== '') {
    payload.createdOn = f.createdOn;
  }
  if (f.currentUserData && typeof f.currentUserData === 'object') {
    payload.currentUserData = f.currentUserData;
  }

  return payload;
}

/** URL for POST .../branches/:branchId/customer-fees/actions/delete-fee */
export function deleteFeeUrl(branchId: string): string {
  return `branches/${branchId}/customer-fees/actions/delete-fee`;
}

/** URL for POST .../branches/:branchId/customer-fees/actions/pay-request-fund */
export function payReqFundUrl(branchId: string): string {
  return `branches/${branchId}/customer-fees/actions/pay-request-fund`;
}

/** URL for POST .../branches/:branchId/customer-fees/actions/add-freezing-fee-and-days */
export function addFreezingFeeAndDaysUrl(branchId: string): string {
  return `branches/${branchId}/customer-fees/actions/add-freezing-fee-and-days`;
}

/** URL for POST .../branches/:branchId/customers/actions/pay-fund-split */
export function payFundSplitUrl(branchId: string): string {
  return `branches/${branchId}/customers/actions/pay-fund-split`;
}

/**
 * Body for POST .../customers/actions/pay-fund-split (PayFundSplitDto).
 * `customer` and `fund` pass through unchanged (same shape as pay-fund);
 * split lines are stripped to the whitelisted SplitLineDto fields and their
 * `pay` must already be GROSS amounts summing to fund.pay.
 */
export function buildPayFundSplitPayload(
  customer: Record<string, unknown>,
  fund: Record<string, unknown>,
  splits: Array<{
    paymentMode: string;
    pay: number | null;
    tax?: number | string;
    transactionNumber?: string;
    feeReceiptImageUrl?: string;
  }>,
): Record<string, unknown> {
  return {
    customer,
    fund,
    splits: splits.map((line) => {
      const cleaned: Record<string, unknown> = {
        paymentMode: line.paymentMode,
        pay: Math.round(Number(line.pay) || 0),
      };
      if (line.tax !== undefined && line.tax !== null && line.tax !== '') {
        cleaned.tax = Math.round(Number(line.tax) || 0);
      }
      if (line.transactionNumber) cleaned.transactionNumber = line.transactionNumber;
      if (line.feeReceiptImageUrl) cleaned.feeReceiptImageUrl = line.feeReceiptImageUrl;
      return cleaned;
    }),
  };
}

/** URL for POST .../branches/:branchId/customer-fees/actions/billing-status */
export function billingStatusUrl(branchId: string): string {
  return `branches/${branchId}/customer-fees/actions/billing-status`;
}

/** URL for POST .../branches/:branchId/customer-fees/actions/trainer-billing-status */
export function trainerBillingStatusUrl(branchId: string): string {
  return `branches/${branchId}/customer-fees/actions/trainer-billing-status`;
}

export function buildTrainerBillingStatusPayload(
  customerId: string,
  payAdvance = false,
): Record<string, unknown> {
  return { customerId, payAdvance };
}

/** URL for POST .../branches/:branchId/customer-fees/actions/billing-periods */
export function billingPeriodsUrl(branchId: string): string {
  return `branches/${branchId}/customer-fees/actions/billing-periods`;
}

export function buildBillingStatusPayload(
  customerId: string,
  payAdvance = false,
  payNewPeriod = false,
): Record<string, unknown> {
  return { customerId, payAdvance, payNewPeriod };
}

const FEE_STATUS_MONTHLY = 'Monthly Fee - Paid';
const FEE_STATUS_TRAINER = 'Trainer Fee - Paid';
const FEE_STATUS_SUFFIX = ' - Paid';

function formatBillingPeriodDate(value: unknown): string | null {
  const d = parseLocalBillingDate(value as string | Date | null | undefined);
  if (!d) return null;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });
}

export function formatInvoiceBillingDate(value: unknown): string {
  const d = parseLocalBillingDate(value as string | Date | null | undefined);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Invoice "Due Date" / "Next Due" rows — prefer fee previousDueDate / nextDueDate. */
export function resolveInvoicePeriodDates(input: {
  status?: string;
  billingPeriodStart?: unknown;
  billingPeriodEnd?: unknown;
  previousDueDate?: unknown;
  nextDueDate?: unknown;
  dueDate?: unknown;
  day?: unknown;
  month?: unknown;
  year?: unknown;
}): { periodStart: Date | null; periodEnd: Date | null } {
  const feePrevious = parseLocalBillingDate(input.previousDueDate);
  const feeNext = parseLocalBillingDate(input.nextDueDate);
  if (feePrevious && feeNext) {
    return { periodStart: feePrevious, periodEnd: feeNext };
  }

  if (input.status === FEE_STATUS_MONTHLY) {
    const periodStart = parseLocalBillingDate(
      feePrevious ??
        input.billingPeriodStart ??
        input.previousDueDate,
    );
    const periodEnd = parseLocalBillingDate(
      feeNext ?? input.billingPeriodEnd ?? input.nextDueDate,
    );
    if (periodStart && periodEnd) {
      return { periodStart, periodEnd };
    }
  }

  const legacyStart =
    input.day != null &&
    input.month != null &&
    input.year != null &&
    String(input.day) !== '' &&
    Number(input.month) > 0 &&
    Number(input.year) > 0
      ? parseLocalBillingDate(
          `${input.year}-${String(input.month).padStart(2, '0')}-${String(input.day).padStart(2, '0')}`,
        )
      : parseLocalBillingDate(input.previousDueDate ?? input.dueDate);
  const legacyEnd = parseLocalBillingDate(input.dueDate ?? input.nextDueDate);
  return { periodStart: legacyStart, periodEnd: legacyEnd };
}

type FeeInvoicePeriodInput = {
  status?: string;
  billingPeriodStart?: unknown;
  billingPeriodEnd?: unknown;
  previousDueDate?: unknown;
  nextDueDate?: unknown;
  dueDate?: unknown;
  day?: unknown;
  month?: unknown;
  year?: unknown;
};

/**
 * Receipt / reprint "Due Date" row only (does not change stored previousDueDate).
 * Later cycles: previousDueDate is prior period end → show +1 day.
 * First fee: previousDueDate already equals billingPeriodStart → show as-is.
 */
export function formatFeeInvoicePeriodStart(fee: FeeInvoicePeriodInput): string {
  const prev =
    parseLocalBillingDate(fee.previousDueDate) ??
    resolveInvoicePeriodDates(fee).periodStart;
  if (!prev) return '';

  const periodStart = parseLocalBillingDate(fee.billingPeriodStart);
  if (periodStart && prev.getTime() === periodStart.getTime()) {
    return formatInvoiceBillingDate(prev);
  }
  return formatInvoiceBillingDate(billingAddDays(prev, 1));
}

/** Fee-details / reprint invoice — fee row nextDueDate (Next Due row). */
export function formatFeeInvoicePeriodEnd(fee: FeeInvoicePeriodInput): string {
  return formatInvoiceBillingDate(
    parseLocalBillingDate(fee.nextDueDate) ??
      resolveInvoicePeriodDates(fee).periodEnd,
  );
}

/** Display label for fee table: strips trailing " - Paid" from stored status. */
export function getFeeTypeDisplay(status?: string): string {
  if (!status) return '-';
  return status.endsWith(FEE_STATUS_SUFFIX)
    ? status.slice(0, -FEE_STATUS_SUFFIX.length)
    : status;
}

/** Parse billing dates as local calendar days (YYYY-MM-DD must not use UTC midnight). */
export function parseLocalBillingDate(
  value: unknown,
): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }
  const trimmed = String(value).trim();
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  if (isDateOnly) {
    const d = new Date(
      Number(trimmed.slice(0, 4)),
      Number(trimmed.slice(5, 7)) - 1,
      Number(trimmed.slice(8, 10)),
    );
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

export type FeeRequestPeriodDecision = 'allow' | 'reject' | 'legacy';

/**
 * Same rules as tecfit-backend fee-request-period.util.
 * `legacy` means the last fee has no billing period.
 */
export function evaluateFeeRequestPeriod(input: {
  requestPeriodStart?: unknown;
  requestPeriodEnd?: unknown;
  lastFee?: {
    billingPeriodStart?: unknown;
    billingPeriodEnd?: unknown;
    balance?: unknown;
  } | null;
}): FeeRequestPeriodDecision {
  const requestEnd = parseLocalBillingDate(input.requestPeriodEnd);
  if (!requestEnd) {
    return 'legacy';
  }
  if (!input.lastFee) {
    return 'allow';
  }
  const lastEnd = parseLocalBillingDate(input.lastFee.billingPeriodEnd);
  if (!lastEnd) {
    return 'legacy';
  }
  if (lastEnd.getTime() > requestEnd.getTime()) {
    return 'reject';
  }
  if (lastEnd.getTime() < requestEnd.getTime()) {
    return 'allow';
  }
  const requestStart = parseLocalBillingDate(input.requestPeriodStart);
  const lastStart = parseLocalBillingDate(input.lastFee.billingPeriodStart);
  if (requestStart && lastStart && requestStart.getTime() !== lastStart.getTime()) {
    return 'allow';
  }
  const balance = Number(input.lastFee.balance);
  if (Number.isFinite(balance) && balance > 0) {
    return 'allow';
  }
  return 'reject';
}

function startOfDayTime(value: unknown): number | null {
  const d = parseLocalBillingDate(value as string | Date | null | undefined);
  return d?.getTime() ?? null;
}

/**
 * Next due / paid-through date shown when collecting a monthly fee.
 * Prefers billingPeriodEnd (matches backend prepareMonthlyFeeSave on full pay).
 */
export function resolveFeePaidUntilRaw(
  settingsDueDate?: string | Date | null,
  billingPeriodEnd?: string | Date | null,
): string | Date | null {
  const dueT = startOfDayTime(settingsDueDate);
  const endT = startOfDayTime(billingPeriodEnd);
  if (endT != null) {
    if (dueT == null || dueT <= endT) {
      return billingPeriodEnd ?? null;
    }
  }
  if (settingsDueDate != null && settingsDueDate !== '') {
    return settingsDueDate;
  }
  return billingPeriodEnd ?? null;
}

function billingStartOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function billingAddDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return billingStartOfDay(result);
}

/** Inclusive calendar days between period start and end (matches backend). */
export function inclusiveBillingDayCount(start: Date, end: Date): number {
  const s = billingStartOfDay(start).getTime();
  const e = billingStartOfDay(end).getTime();
  if (e < s) return 0;
  return Math.floor((e - s) / 86400000) + 1;
}

/** Scale a package fee to a shorter billing end. The original end keeps the full fee. */
export function prorateBillingPackageFee(
  originalFee: number,
  originalStart: Date,
  originalEnd: Date,
  editedEnd: Date,
): number {
  const fee = Math.round(Number(originalFee) || 0);
  const originalDays = inclusiveBillingDayCount(originalStart, originalEnd);
  const editedDays = inclusiveBillingDayCount(originalStart, editedEnd);
  if (originalDays <= 0 || editedDays <= 0 || editedDays === originalDays) {
    return fee;
  }
  return Math.round((fee * editedDays) / originalDays);
}

/**
 * Day-prorated pre-tax fee when staff shorten the period end.
 * Full end returns fullFee. Uses the same inclusive day count as monthly periods.
 */
export function prorateFeeForShortenedEnd(input: {
  periodStart: Date;
  originalEnd: Date;
  selectedEnd: Date;
  fullFee: number;
}): number {
  const fullFee = Math.max(0, Math.round(Number(input.fullFee) || 0));
  const fullDays = inclusiveBillingDayCount(input.periodStart, input.originalEnd);
  const usedDays = inclusiveBillingDayCount(input.periodStart, input.selectedEnd);
  if (fullDays <= 0 || usedDays >= fullDays) return fullFee;
  if (usedDays <= 0) return 0;
  return Math.round((fullFee * usedDays) / fullDays);
}

export type PackageDurationInput = {
  durationType?: string;
  noOfMonths?: number;
  noOfDays?: number;
  noOfWeeks?: number;
};

function billingDaysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/** Calendar days in one billing cycle (matches backend computePeriodDays). */
export function computePeriodDays(
  periodStart: Date,
  packageInfo?: PackageDurationInput | null,
): number {
  const pkg = packageInfo ?? { durationType: 'months', noOfMonths: 1 };
  const durationType = pkg.durationType ?? 'months';
  const numOfMonths = Number(pkg.noOfMonths) > 0 ? Number(pkg.noOfMonths) : 1;
  const noOfDays = Number(pkg.noOfDays) > 0 ? Number(pkg.noOfDays) : 0;
  const noOfWeeks = Number(pkg.noOfWeeks) > 0 ? Number(pkg.noOfWeeks) : 0;

  if (durationType === 'days') {
    return Math.max(1, noOfDays);
  }
  if (durationType === 'weeks') {
    return Math.max(1, noOfWeeks * 7);
  }
  if (numOfMonths >= 2) {
    let total = 0;
    let cursor = billingStartOfDay(periodStart);
    for (let i = 0; i < numOfMonths; i++) {
      total += billingDaysInMonth(cursor);
      cursor = billingAddDays(cursor, billingDaysInMonth(cursor));
    }
    return Math.max(1, total);
  }
  return billingDaysInMonth(periodStart);
}

/** Paid-through date for a billing cycle (matches backend computeBillingPeriodEnd). */
export function computeBillingPeriodEnd(
  periodStart: Date,
  packageInfo?: PackageDurationInput | null,
): Date {
  const periodDays = computePeriodDays(periodStart, packageInfo);
  return billingAddDays(billingStartOfDay(periodStart), periodDays - 1);
}

export function computeBillingPeriodBounds(
  periodStart: Date,
  packageInfo?: PackageDurationInput | null,
): {
  billingPeriodStart: Date;
  billingPeriodEnd: Date;
  periodDays: number;
} {
  const start = billingStartOfDay(periodStart);
  const end = computeBillingPeriodEnd(start, packageInfo);
  return {
    billingPeriodStart: start,
    billingPeriodEnd: end,
    periodDays: inclusiveBillingDayCount(start, end),
  };
}

/** YYYY-MM-DD for fund due dates (avoids UTC shift from toISOString). */
export function toBillingDateString(value: Date): string {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Prorated paid-through date within a billing period (matches backend). */
export function computeProratedDueDate(input: {
  billingPeriodStart: Date;
  billingPeriodEnd: Date;
  netPackageFee: number;
  cumulativePaid: number;
}): Date {
  const { billingPeriodStart, billingPeriodEnd, netPackageFee, cumulativePaid } =
    input;
  const periodDays = inclusiveBillingDayCount(
    billingPeriodStart,
    billingPeriodEnd,
  );
  if (netPackageFee <= 0 || cumulativePaid <= 0) {
    return billingStartOfDay(billingPeriodStart);
  }
  if (cumulativePaid >= netPackageFee) {
    return billingStartOfDay(billingPeriodEnd);
  }
  const ratio = cumulativePaid / netPackageFee;
  const daysToAdd = Math.round(ratio * periodDays) - 1;
  const clamped = Math.max(0, Math.min(daysToAdd, periodDays - 1));
  return billingAddDays(billingPeriodStart, clamped);
}

/** Preview next due date on Add Fee when paying a partial monthly fee. */
export function resolveMonthlyFeeDueDatePreview(input: {
  billingPeriodStart?: unknown;
  billingPeriodEnd?: unknown;
  netPackageFee: number;
  alreadyPaid: number;
  payAmount: number;
  discountAmount: number;
}): Date | null {
  const periodStart = parseLocalBillingDate(input.billingPeriodStart);
  const periodEnd = parseLocalBillingDate(input.billingPeriodEnd);
  if (!periodStart || !periodEnd) return null;

  const netPackageFee = Math.max(
    0,
    Number(input.netPackageFee) - (Number(input.discountAmount) || 0),
  );
  const cumulativePaid =
    (Number(input.alreadyPaid) || 0) + (Number(input.payAmount) || 0);
  const balanceAfter = Math.max(0, netPackageFee - cumulativePaid);

  if (balanceAfter === 0) {
    return periodEnd;
  }
  return computeProratedDueDate({
    billingPeriodStart: periodStart,
    billingPeriodEnd: periodEnd,
    netPackageFee,
    cumulativePaid,
  });
}

function hasStoredBillingDate(value: unknown): boolean {
  return value != null && String(value).trim() !== '';
}

function formatBillingPeriodRange(start: unknown, end: unknown): string | null {
  const startLabel = formatBillingPeriodDate(start);
  const endLabel = formatBillingPeriodDate(end);
  if (!startLabel || !endLabel) return null;
  return `${startLabel} - ${endLabel}`;
}

/** Billing period for monthly and trainer fees; "-" for registration, fine, etc. */
export function formatFeeBillingPeriod(fee: {
  status?: string;
  billingPeriodStart?: string | Date;
  billingPeriodEnd?: string | Date;
  previousDueDate?: string | Date;
  date?: string | Date;
  nextDueDate?: string | Date;
}): string {
  if (fee.status === FEE_STATUS_TRAINER) {
    if (
      hasStoredBillingDate(fee.billingPeriodStart) &&
      hasStoredBillingDate(fee.billingPeriodEnd)
    ) {
      return (
        formatBillingPeriodRange(fee.billingPeriodStart, fee.billingPeriodEnd) ??
        '-'
      );
    }
    return (
      formatBillingPeriodRange(fee.previousDueDate, fee.nextDueDate) ?? '-'
    );
  }
  if (fee.status !== FEE_STATUS_MONTHLY) return '-';
  const start =
    fee.billingPeriodStart ??
    (fee.previousDueDate != null && String(fee.previousDueDate).trim() !== ''
      ? fee.previousDueDate
      : null) ??
    fee.date;
  const end = fee.billingPeriodEnd ?? fee.nextDueDate;
  return formatBillingPeriodRange(start, end) ?? '-';
}

function isLatestFeeInStatus(
  allFees: Array<Record<string, unknown>>,
  target: Record<string, unknown>,
  feeId: string,
  status: string,
): boolean {
  const active = allFees.filter(
    (f) =>
      !f.isDelete &&
      f.status === status &&
      f.isSecondaryPackageFee !== true,
  );
  const byDate = [...active].sort((a, b) => {
    const da = new Date((a.date as string | Date) ?? 0).getTime();
    const db = new Date((b.date as string | Date) ?? 0).getTime();
    return db - da;
  });
  const newest = byDate[0];
  if (!newest) return true;
  const newestGroup = newest.splitGroupId
    ? String(newest.splitGroupId)
    : null;
  const targetGroup = target.splitGroupId
    ? String(target.splitGroupId)
    : null;
  if (newestGroup && targetGroup === newestGroup) return true;
  if (!newestGroup && !targetGroup) {
    return String(newest._id ?? newest.id) === String(feeId);
  }
  return false;
}

/** Client-side LIFO check for primary monthly fees and trainer fees. */
export function canDeleteFeeLifo(
  allFees: Array<Record<string, unknown>>,
  feeId: string,
): boolean {
  const target = allFees.find(
    (f) =>
      !f.isDelete && String(f._id ?? f.id) === String(feeId),
  );
  if (!target) return false;
  if (target.isSecondaryPackageFee === true) return true;
  if (
    target.status === FEE_STATUS_MONTHLY ||
    target.status === FEE_STATUS_TRAINER
  ) {
    return isLatestFeeInStatus(
      allFees,
      target,
      feeId,
      String(target.status),
    );
  }
  return true;
}

const FEE_STATUS_MONTHLY_DELETE = 'Monthly Fee - Paid';

/** Delete confirmation: split payment, same-history group monthly, or single fee. */
export function getFeeDeleteConfirmationMessage(
  fee: Record<string, unknown>,
  feeDetails: Array<Record<string, unknown>> | null | undefined,
  messages: {
    single: string;
    split: string;
    sameHistory?: string;
    sameHistoryMemberCount?: number;
    useSameHistory?: boolean;
  },
): string {
  const splitGroup = getSplitGroupFees(feeDetails, fee);
  if (splitGroup.length > 1) {
    return messages.split.replace(/\{count\}/g, String(splitGroup.length));
  }
  const memberCount = Math.max(2, Number(messages.sameHistoryMemberCount) || 2);
  if (
    messages.useSameHistory &&
    fee.status === FEE_STATUS_MONTHLY_DELETE &&
    messages.sameHistory
  ) {
    return messages.sameHistory.replace(/\{count\}/g, String(memberCount));
  }
  return messages.single;
}

/** Newest approved monthly fee row (matches backend LIFO / billing-status sort). */
export function findLastMonthlyFeeRow(
  allFees: Array<Record<string, unknown>>,
): Record<string, unknown> | null {
  const active = allFees.filter(
    (f) =>
      !f.isDelete &&
      f.isApproved !== false &&
      f.status === FEE_STATUS_MONTHLY,
  );
  if (active.length === 0) return null;
  return [...active].sort((a, b) => {
    const da = new Date((a.date as string | Date) ?? 0).getTime();
    const db = new Date((b.date as string | Date) ?? 0).getTime();
    if (db !== da) return db - da;
    const ca = new Date((a.createdOn as string | Date) ?? 0).getTime();
    const cb = new Date((b.createdOn as string | Date) ?? 0).getTime();
    return cb - ca;
  })[0];
}

/**
 * Earliest calendar date staff may set manually from the last monthly fee:
 * - open partial → nextDueDate (prorated paid-through)
 * - fully paid → billingPeriodEnd
 * Returns null when no fees or dates missing.
 */
export function getMinAllowedManualDueDate(
  allFees: Array<Record<string, unknown>>,
): Date | null {
  const last = findLastMonthlyFeeRow(allFees);
  if (!last) return null;
  const balance = Number(last.balance ?? 0);
  const endRaw =
    balance > 0
      ? last.nextDueDate ?? last.billingPeriodEnd
      : last.billingPeriodEnd ?? last.nextDueDate;
  if (endRaw == null || endRaw === '') return null;
  const end = new Date(endRaw as string | Date);
  if (Number.isNaN(end.getTime())) return null;
  end.setHours(0, 0, 0, 0);
  return end;
}

export function formatPaidThroughLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Client-side guard mirroring backend validateManualMemberDueDate. */
export function isManualDueDateBeforePaidPeriod(
  candidate: unknown,
  allFees: Array<Record<string, unknown>>,
): { blocked: boolean; paidThrough: Date | null } {
  const paidThrough = getMinAllowedManualDueDate(allFees);
  if (!paidThrough || candidate == null || candidate === '') {
    return { blocked: false, paidThrough };
  }
  const d = new Date(candidate as string | Date);
  if (Number.isNaN(d.getTime())) {
    return { blocked: false, paidThrough };
  }
  d.setHours(0, 0, 0, 0);
  return {
    blocked: d.getTime() < paidThrough.getTime(),
    paidThrough,
  };
}

/** True when member due date is today or in the future (package change blocked). */
export function isBillingPeriodActive(dueDate: unknown): boolean {
  if (dueDate == null || dueDate === '') return false;
  const due = new Date(dueDate as string | Date);
  if (Number.isNaN(due.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return today.getTime() <= due.getTime();
}

/** URL for POST .../branches/:branchId/customer-fees/reports/monthly-fee */
export function monthlyFeeReportUrl(branchId: string): string {
  return `branches/${branchId}/customer-fees/reports/monthly-fee`;
}

/** URL for POST .../branches/:branchId/customer-fees/reports/trainer-monthly-fee */
export function trainerMonthlyFeeReportUrl(branchId: string): string {
  return `branches/${branchId}/customer-fees/reports/trainer-monthly-fee`;
}

/** Current calendar month (1–12) and year for fee month/year dropdowns. */
export function getCurrentFeeMonthYear(): { month: number; year: number } {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

/** Default fund.month / fund.year to today; does not derive from due date. */
export function applyDefaultFundMonthYear(
  fund: { month?: number | null; year?: number | null },
  force = false,
): void {
  const current = getCurrentFeeMonthYear();
  if (force || fund.month == null || fund.month === undefined) {
    fund.month = current.month;
  }
  if (force || fund.year == null || fund.year === undefined) {
    fund.year = current.year;
  }
}

/**
 * Legacy monthly-fee report body: [memberId, fund with selected month/year, noOfMonths?].
 * Pass billingPeriodStart (member.settings.dueDate) so the backend can exclude
 * fees from a non-overlapping prior cycle in the same calendar month.
 */
export function buildMonthlyFeeReportPayload(
  memberId: string,
  fund: Record<string, unknown>,
  numOfMonths?: number,
  billingPeriodStart?: unknown,
): [string, Record<string, unknown>, number?] {
  const fundBody: Record<string, unknown> = {
    ...fund,
    month: fund.month,
    year: fund.year,
  };
  if (billingPeriodStart != null && billingPeriodStart !== '') {
    fundBody.oldDate = billingPeriodStart;
  }
  const payload: [string, Record<string, unknown>, number?] = [
    memberId,
    fundBody,
  ];
  if (numOfMonths !== undefined && numOfMonths !== null) {
    payload[2] = numOfMonths;
  }
  return payload;
}
